'use client'

import { useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { COMPANY_LOGO_BLUE } from '@/lib/constants'
import { denyConsent, grantConsent, needsConsentPrompt } from '@/lib/consent/client'
import { useIsHydrated } from '@/hooks/use-is-hydrated'

/**
 * ConsentBanner — the ask, shown ONLY where the signal says it is needed
 * (FRIDGE-115).
 *
 * `needsConsentPrompt()` is true only when the middleware marked this
 * visitor's country consent-gated (EEA + UK + CH, lib/consent/signal.ts) AND
 * no choice is stored yet. Everywhere else this component renders null, so
 * the US page is unchanged — nothing paints, nothing shifts.
 *
 * Because the gate is read from a cookie, the check must happen after mount:
 * the server render is identical for every visitor (the HTML stays cacheable
 * on the CDN) and there is no hydration mismatch.
 *
 * The choice is what the tags obey — PostHog and Sentry do not initialise
 * before Allow, and Google Consent Mode sits at denied until then — so this
 * is a functional control, not a decorative notice.
 *
 * Design: light surface, brand accent bar, matching the newsletter card's
 * shape. Bottom-left so it never collides with that card (bottom-right).
 */
const ORANGE = '#ff9951'
const ORANGE_HOVER = '#ff8533'

/**
 * The /embed widgets (FRIDGE-356) are framed inside OTHER sites' pages. The
 * ask never renders there: a consent dialog inside a 300px-wide widget on a
 * publisher's page would read as the publisher's, and no answer is recorded,
 * so a gated visitor stays at the default — denied — and nothing initialises.
 */
function isEmbedPath(pathname: string | null): boolean {
  return pathname === '/embed' || (pathname !== null && pathname.startsWith('/embed/'))
}

export function ConsentBanner() {
  const pathname = usePathname()
  // Whether to prompt is DERIVED, not pushed into state from an effect
  // (react-hooks/set-state-in-effect). needsConsentPrompt() reads the cookie,
  // so it is gated on hydration: the server render and the hydration pass both
  // see false, exactly as the old useState(false) + effect did, and the banner
  // is still absent from the server HTML.
  //
  // `dismissed` carries the only genuinely stateful part — Allow and Deny hide
  // the banner for the rest of the visit.
  const hydrated = useIsHydrated()
  const [dismissed, setDismissed] = useState(false)
  const visible =
    hydrated && !dismissed && !isEmbedPath(pathname) && needsConsentPrompt()

  // The slide-in still runs a frame after the banner mounts, so the transition
  // plays instead of the banner appearing already in place.
  const [entered, setEntered] = useState(false)
  useEffect(() => {
    if (!visible) return
    const id = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(id)
  }, [visible])

  const allow = useCallback(() => {
    grantConsent()
    setDismissed(true)
  }, [])

  const decline = useCallback(() => {
    denyConsent()
    setDismissed(true)
  }, [])

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="consent-banner-title"
      data-testid="consent-banner"
      className={[
        'fixed z-50 bottom-4 left-4 right-4 sm:right-auto sm:max-w-md',
        'rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden',
        'transition-all duration-300 ease-out motion-reduce:transition-none',
        entered ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
      ].join(' ')}
    >
      <div
        aria-hidden="true"
        className="h-1 w-full"
        style={{ background: `linear-gradient(90deg, ${ORANGE} 0%, ${ORANGE_HOVER} 100%)` }}
      />
      <div className="p-4 sm:p-5">
        <div className="flex items-center gap-2">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={COMPANY_LOGO_BLUE} alt="Fridge.com" className="h-5 w-auto" width={120} height={20} />
          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: ORANGE_HOVER }}>
            Your choice
          </span>
        </div>

        <p id="consent-banner-title" className="mt-2 text-[15px] font-bold tracking-tight text-gray-900">
          Allow analytics cookies?
        </p>
        <p className="mt-1 text-[13px] leading-relaxed text-gray-600">
          We would use Google Analytics, PostHog and Sentry to measure how the site is used and to see when
          something breaks. Decline and none of them run. You can change this on the{' '}
          <Link href="/privacy-policy#cookies" className="underline hover:text-gray-900">
            privacy policy
          </Link>{' '}
          page. We never serve advertising.
        </p>

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={allow}
            data-testid="consent-allow"
            className="flex-1 rounded-lg px-4 py-2 text-sm font-semibold text-[var(--color-brand-action-text)] transition-colors"
            style={{ background: ORANGE_HOVER }}
          >
            Allow
          </button>
          <button
            type="button"
            onClick={decline}
            data-testid="consent-decline"
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConsentBanner
